import * as React from "react";
import type { RegistrationsErrorResponse } from "src/types";

import type { SimpleFlowResponse } from "@/features/authentication/authentication-reducer";
import { useAuthentication } from "@/hooks/use-authentication";
import useEvent from "@/hooks/use-event";
import endpoints from "@/utils/endpoints";
import { authenticatedFetch } from "@/utils/fetchJSON";
import { isRegistrationError } from "@/utils/is-error";
import logger from "@/utils/logger";

const get = endpoints();

export type SimpleFlowRedirectProps = {
  isOpen?: boolean;
  onClose: () => void;
  renderWidget: (isOpen: boolean, onClose: () => void) => JSX.Element;
};

const SimpleFlowRedirect = ({
  isOpen = false,
  onClose,
  renderWidget,
}: SimpleFlowRedirectProps) => {
  const { event } = useEvent();
  const [isModalOpen, setModalOpen] = React.useState(false);

  const {
    hopinUser,
    isHopinUserRegistered,
    isSimpleFlow,
    eventAccessUrl,
  } = useAuthentication({
    slug: event.slug,
  });

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    async function registerAndRedirect() {
      try {
        const response = await authenticatedFetch.fetchJSON<
          SimpleFlowResponse,
          RegistrationsErrorResponse
        >(
          get("REGISTRATION_VERIFY_SIMPLE_FLOW_AND_REGISTER")(
            event.slug as string
          )
        );

        if (isRegistrationError(response)) {
          setModalOpen(true);
          return;
        }

        window.location.assign(response?.reception_url);
      } catch (simpleFlowError) {
        setModalOpen(true);

        logger.error(simpleFlowError, {
          details: "Failed to check simple flow for registration",
        });
      }
    }

    if (isHopinUserRegistered) {
      window.location.assign(eventAccessUrl);
      return;
    }

    if (!(isSimpleFlow && event.require_sso_login)) {
      if (!isModalOpen || hopinUser === null) {
        setModalOpen(true);
        return;
      }
    }

    if (isSimpleFlow) {
      registerAndRedirect();
      return;
    }

    setModalOpen(true);
  }, [
    isModalOpen,
    isOpen,
    isHopinUserRegistered,
    isSimpleFlow,
    eventAccessUrl,
    event.slug,
    hopinUser,
    event.require_sso_login,
  ]);

  if (!isModalOpen) {
    return null;
  }

  return renderWidget(isModalOpen, () => {
    setModalOpen(false);
    onClose();
  });
};

export default SimpleFlowRedirect;
